/**
 * Import price limits from CSV — DS native or RateGain / Carwiz, auto
 * detected (CU-869fkbjdh § 3.8). The preview shows the parsed rows in the
 * SAME four columns as the list; Append / Replace all as a selector with
 * names. The parsers live in `@services/priceLimitsCsv` (pure); only the
 * two things that touch the DOM — reading a file, downloading — are here.
 */
import React, { useMemo, useRef, useState } from 'react'
import { Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import {
	AppBulkEditAction, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppIconChipToggle, AppNote,
	secondaryButtonSx,
} from '@components'
import { dsColorNew } from '@palette'
import { UploadFileOutlined as UploadIcon, DownloadOutlined as DownloadIcon } from 'Pages/DsRepricing/@components/icons'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { inputSx, cellPadSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { useAxisCatalog } from 'Pages/DsRepricing/Setup/@services'
import { DS_CSV_HEADERS, DS_CSV_TEMPLATE_ROWS, parseCsv, toCsv } from 'Pages/DsRepricing/Setup/@services/priceLimitsCsv'
import { HeadCell, WhereCell, WhenCell, FloorCell, CeilingCell } from './cells'

const PREVIEW_ROWS = 8

function download(name, csv) {
	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = name
	document.body.appendChild(a)
	a.click()
	document.body.removeChild(a)
	URL.revokeObjectURL(url)
}

/** Export — the list as the DS template, ids not names, so it round-trips through the importer. */
export function downloadCsv(limits) {
	download(`price-limits-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(limits))
}

export default function CsvImportDialog({ open, onClose, onImport, labels, matrixById, existing = 0 }) {
	const [text, setText] = useState('')
	const [fileName, setFileName] = useState('')
	const [mode, setMode] = useState('append')
	const fileInputRef = useRef(null)
	const axes = useAxisCatalog()
	const parsed = useMemo(() =>
		text ? parseCsv(text, axes) : { rows: [], errors: [], warnings: [], format: null }, [text, axes])

	function onFile(e) {
		const f = e.target.files?.[0]
		if (!f) return
		setFileName(f.name)
		const reader = new FileReader()
		reader.onload = () => setText(reader.result)
		reader.readAsText(f)
	}
	function downloadTemplate() {
		download('price-limits-template.csv', [DS_CSV_HEADERS.join(','), ...DS_CSV_TEMPLATE_ROWS.map(r => r.join(','))].join('\n'))
	}
	const reset = () => { setText(''); setFileName(''); setMode('append') }
	const close = () => { reset(); onClose() }
	const n = parsed.rows.length
	const plural = (k, w) => `${k} ${w}${k === 1 ? '' : 's'}`
	const detected = parsed.format === 'rategain' ? 'RateGain · Carwiz' : parsed.format === 'ds' ? 'DS native' : null

	return (
		<AppDialog open={open} onClose={close} maxWidth="lg" paperSx={setupScopeSx}>
			<AppDialogHeader icon="file-arrow-up" title="Import price limits" onClose={close}
			                 subtitle={detected
			                   ? <>DS format or a RateGain / Carwiz export — detected: <strong>{detected}</strong> · {plural(n, 'rule')} parsed · {plural(parsed.errors.length, 'error')}</>
			                   : 'DS format or a RateGain / Carwiz export, auto-detected'}/>
			<AppDialogBody sx={dialogBodySx}>
				<Stack gap={2}>
					<Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
						<input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onFile} style={{ display: 'none' }}/>
						<Button variant="outlined" startIcon={<UploadIcon/>} onClick={() => fileInputRef.current?.click()}
						        sx={{ ...secondaryButtonSx, height: 36 }}>
							Choose file
						</Button>
						{fileName && <Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle }}>{fileName}</Typography>}
						<Button variant="text" startIcon={<DownloadIcon/>} onClick={downloadTemplate}
						        sx={{ textTransform: 'none', fontWeight: 500, fontSize: 13, color: dsColorNew.appBlue }}>
							Download the template
						</Button>
						<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.25 }}>
							<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle }}>
								What to do with the {plural(existing, 'rule')} you have
							</Typography>
							<AppIconChipToggle size="small" value={mode} onChange={setMode}
							                   options={[{ value: 'append', label: 'Append', icon: 'plus' },
							                             { value: 'replace', label: 'Replace all', icon: 'arrows-rotate' }]}/>
						</Box>
					</Stack>

					<TextField multiline minRows={4} maxRows={10}
					           placeholder="…or paste CSV content here"
					           value={text}
					           onChange={(e) => setText(e.target.value)}
					           sx={{ bgcolor: dsColorNew.white, ...inputSx, '& .MuiOutlinedInput-input': { fontFamily: 'inherit', fontSize: 12 } }}/>

					{!parsed.format && (
						<AppNote severity="info">
							<strong>DS native</strong> — the four columns of the list plus <code>protects_dates</code> and <code>name</code> for a
							date protection (headers: {DS_CSV_HEADERS.join(', ')}). <strong>RateGain / Carwiz</strong> — the partner file:
							Brand → franchise, ProductCode → tariff type, LOR breakpoints become rental-length ranges; it ships no insurance,
							so that scope comes empty.
						</AppNote>
					)}

					{parsed.errors.length > 0 && (
						<AppNote severity="error">
							<Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>
								{plural(parsed.errors.length, 'error')} to fix:
							</Typography>
							{parsed.errors.slice(0, 5).map((e, i) => <Typography key={i} sx={{ fontSize: 12 }}>• {e}</Typography>)}
							{parsed.errors.length > 5 && <Typography sx={{ fontSize: 12, mt: 0.5 }}>…and {parsed.errors.length - 5} more.</Typography>}
						</AppNote>
					)}
					{parsed.warnings?.length > 0 && (
						<AppNote severity="warning">
							<Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>Notes:</Typography>
							{parsed.warnings.map((w, i) => <Typography key={i} sx={{ fontSize: 12 }}>• {w}</Typography>)}
						</AppNote>
					)}

					{n > 0 && (
						<TableContainer sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', bgcolor: dsColorNew.white }}>
							<Table size="small" sx={{ tableLayout: 'fixed' }}>
								<TableHead>
									<TableRow>
										<HeadCell>Where</HeadCell>
										<HeadCell sx={{ width: 300 }}>When</HeadCell>
										<HeadCell align="right" sx={{ width: 150 }}>Floor €/day</HeadCell>
										<HeadCell align="right" sx={{ width: 130 }}>Ceiling €/day</HeadCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{parsed.rows.slice(0, PREVIEW_ROWS).map(r => (
										<TableRow key={r.id} sx={{ verticalAlign: 'top', '&:nth-of-type(even)': { bgcolor: dsColorNew.tableRowAlt } }}>
											<TableCell sx={cellPadSx}><WhereCell rule={r} labels={labels}/></TableCell>
											<TableCell sx={cellPadSx}><WhenCell rule={r}/></TableCell>
											<TableCell sx={cellPadSx} align="right"><FloorCell rule={r} matrix={matrixById?.get(String(r.matrix_id))} readOnly/></TableCell>
											<TableCell sx={cellPadSx} align="right"><CeilingCell rule={r} readOnly/></TableCell>
										</TableRow>
									))}
									{n > PREVIEW_ROWS && (
										<TableRow>
											<TableCell colSpan={4} sx={{ ...cellPadSx, color: dsColorNew.textSubtle, fontSize: 12, fontStyle: 'italic' }}>
												… {plural(n - PREVIEW_ROWS, 'more rule')}.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Stack>
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={close}/>
				<AppBulkEditAction disabled={n === 0} onClick={() => { onImport(parsed.rows, mode); reset() }}>
					Import {n ? plural(n, 'rule') : 'rules'}
				</AppBulkEditAction>
			</AppDialogFooter>
		</AppDialog>
	)
}
